import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  Toolbar,
  MenuItem,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import { classAssignmentsAPI, teacherAPI, classesAPI, subjectAPI, academicYearsAPI } from '../../services/api';

export default function ClassAssignments() {
  const [assignments, setAssignments] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    teacher_id: '',
    standard: '',
    section: 'A',
    subject_id: '',
    academic_year_id: '',
    is_class_teacher: 0,
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [assignRes, teachRes, classRes, subjRes, yearRes] = await Promise.all([
        classAssignmentsAPI.getAll(),
        teacherAPI.getAll(),
        classesAPI.getAll(),
        subjectAPI.getAll(),
        academicYearsAPI.getAll(),
      ]);
      setAssignments(assignRes.data);
      setTeachers(teachRes.data);
      setClasses(classRes.data);
      setSubjects(subjRes.data);
      setYears(yearRes.data);
    } catch (error) {
      showSnackbar('Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, type = 'success') => {
    setSnackbar({ open: true, message, type });
  };

  const handleOpenDialog = (assignment = null) => {
    if (assignment) {
      setEditingId(assignment.id);
      setFormData({
        teacher_id: assignment.teacher_id,
        standard: assignment.standard,
        section: assignment.section,
        subject_id: assignment.subject_id || '',
        academic_year_id: assignment.academic_year_id,
        is_class_teacher: assignment.is_class_teacher,
      });
    } else {
      setEditingId(null);
      setFormData({
        teacher_id: '',
        standard: '',
        section: 'A',
        subject_id: '',
        academic_year_id: '',
        is_class_teacher: 0,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  const handleSave = async () => {
    if (!formData.teacher_id || !formData.standard || !formData.academic_year_id) {
      showSnackbar('Teacher, Class, and Academic Year are required', 'error');
      return;
    }

    try {
      if (editingId) {
        await classAssignmentsAPI.update(editingId, formData);
        showSnackbar('Assignment updated successfully', 'success');
      } else {
        await classAssignmentsAPI.create(formData);
        showSnackbar('Assignment created successfully', 'success');
      }
      fetchAllData();
      handleCloseDialog();
    } catch (error) {
      showSnackbar('Error saving assignment', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    try {
      await classAssignmentsAPI.delete(id);
      showSnackbar('Assignment deleted successfully', 'success');
      fetchAllData();
    } catch (error) {
      showSnackbar('Error deleting assignment', 'error');
    }
  };

  const filteredAssignments = assignments.filter(
    (item) =>
      item.teacher_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.standard?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subject_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTeacherName = (id) => teachers.find((t) => t.id === id)?.teacher_name || 'N/A';
  const getSubjectName = (id) => subjects.find((s) => s.id === id)?.subject_name || 'N/A';
  const getYearName = (id) => years.find((y) => y.id === id)?.year_name || 'N/A';

  return (
    <Box sx={{ padding: 2 }}>
      <Toolbar sx={{ paddingLeft: 0, paddingRight: 0, marginBottom: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ marginRight: 2 }}
        >
          Assign Class
        </Button>

        <TextField
          placeholder="Search by teacher, class, or subject..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 350 }}
        />
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell>Teacher</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Year</TableCell>
                <TableCell align="center">Class Teacher</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAssignments.length > 0 ? (
                filteredAssignments.map((item) => (
                  <TableRow key={item.id} hover>
                    <TableCell>{item.teacher_name || getTeacherName(item.teacher_id)}</TableCell>
                    <TableCell>
                      {item.standard} - {item.section}
                    </TableCell>
                    <TableCell>{item.subject_name || getSubjectName(item.subject_id)}</TableCell>
                    <TableCell>{item.year_name || getYearName(item.academic_year_id)}</TableCell>
                    <TableCell align="center">{item.is_class_teacher ? '✓' : '-'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(item)}
                        title="Edit"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(item.id)}
                        title="Delete"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ padding: 3 }}>
                    No assignments found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Assignment' : 'Assign Class'}</DialogTitle>
        <DialogContent sx={{ paddingTop: 2 }}>
          <TextField
            select
            fullWidth
            label="Teacher"
            name="teacher_id"
            value={formData.teacher_id}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            {teachers.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.teacher_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Class/Standard"
            name="standard"
            value={formData.standard}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            {classes.map((c) => (
              <MenuItem key={c.id} value={c.standard}>
                {c.display_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Section"
            name="section"
            value={formData.section}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            {['A', 'B', 'C', 'D'].map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Subject"
            name="subject_id"
            value={formData.subject_id}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            <MenuItem value="">None</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.subject_name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            fullWidth
            label="Academic Year"
            name="academic_year_id"
            value={formData.academic_year_id}
            onChange={handleInputChange}
            sx={{ marginBottom: 2 }}
          >
            {years.map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.year_name}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Checkbox
              name="is_class_teacher"
              checked={formData.is_class_teacher === 1}
              onChange={handleInputChange}
            />
            <span>Mark as Class Teacher</span>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.type}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}
